import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { querySuites } from '@/features/suites/data/suites';
import { useAccessToken } from '@/shared/hooks/auth';
import { queryKeys } from '@/shared/hooks/queryKeys';
import type { ApiError } from '@/shared/data/client';
import type { SuiteListItemDto } from '@flowqa/shared';

type SuiteOption = { value: string; label: string };

type UseSuiteOptionsParams = {
  projectId?: string | null;
  enabled?: boolean;
};

type UseSuiteOptionsResult = { suiteOptions: SuiteOption[]; loading: boolean };

const SUITE_OPTIONS_PAGE_SIZE = 100;

const queryAllSuiteOptions = async (
  projectId: string | null | undefined,
  organisationId: string | undefined,
): Promise<SuiteOption[]> => {
  const items: SuiteListItemDto[] = [];
  let page = 1;
  let total = 0;

  do {
    const result = await querySuites(projectId, organisationId, {
      page,
      pageSize: SUITE_OPTIONS_PAGE_SIZE,
      sorts: [{ field: 'name', direction: 'asc' }],
      filters: [],
    });
    items.push(...result.items);
    total = result.total;
    page += 1;
  } while (items.length < total);

  return items.map((suite) => ({ value: suite.id, label: suite.name }));
};

export const useSuiteOptions = ({
  projectId,
  enabled = true,
}: UseSuiteOptionsParams = {}): UseSuiteOptionsResult => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const suiteOptionsQuery: UseQueryResult<SuiteOption[], ApiError> = useQuery<
    SuiteOption[],
    ApiError
  >({
    queryKey: queryKeys.suiteOptions(projectId, orgKey),
    enabled: Boolean(token) && enabled,
    queryFn: async () => await queryAllSuiteOptions(projectId, selectedOrganisationId ?? undefined),
    staleTime: 30_000,
  });

  const suiteOptions = useMemo(
    () =>
      (suiteOptionsQuery.data ?? []).sort((left, right) => left.label.localeCompare(right.label)),
    [suiteOptionsQuery.data],
  );

  return {
    suiteOptions,
    loading: suiteOptionsQuery.isLoading,
  };
};
